/**
 * Root providers for all expert-review routes.
 *
 * Belongs here: status query, sticky entry `source`, strategy + preferences providers, path params.
 * Does not belong here: page-level handlers or UI.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { StrategyQuestionnaireProvider } from '../strategy-questionnaire/flow-engine/StrategyQuestionnaireProvider'
import { PreferencesProvider } from '../preferences/flow-engine/PreferencesProvider'
import { useQuestionnaireStatusQuery } from '../api/useQuestionnaireStatusQuery'
import { ExpertReviewInitialStatusGate } from './ExpertReviewInitialStatusGate'
import { ExpertReviewSource, ExpertReviewStatusContext } from './ExpertReviewStatusContext'
import {
  parsePreferenceTypeIdFromPath,
  parseStrategyStageIdFromPath
} from '../navigation/expertReviewPaths'

import '../shared/transitions/contentTransition.css'

const SOURCE_VALUES = new Set<string>(Object.values(ExpertReviewSource))

function parseExpertReviewSource(value: string | null): ExpertReviewSource | null {
  if (value != null && SOURCE_VALUES.has(value)) return value as ExpertReviewSource
  return null
}

interface ExpertReviewProvidersProps {
  children: ReactNode
}

export default function ExpertReviewProviders({ children }: ExpertReviewProvidersProps) {
  const location = useLocation()
  const { pathname, search } = location

  const [entrySource, setEntrySource] = useState<ExpertReviewSource | null>(() =>
    parseExpertReviewSource(new URLSearchParams(search).get('source'))
  )

  useEffect(() => {
    const parsed = parseExpertReviewSource(new URLSearchParams(search).get('source'))
    // In-feature routes drop query — only update when URL still carries source
    if (parsed != null) {
      setEntrySource(parsed)
    }
  }, [search])

  const stageIdFromPathParam = useMemo(
    () => parseStrategyStageIdFromPath(pathname),
    [pathname]
  )

  const preferenceTypeIdFromPathParam = useMemo(
    () => parsePreferenceTypeIdFromPath(pathname),
    [pathname]
  )

  // Hub + step cards read status; questionnaires fetch their own payloads in child providers
  const { data: statusData, isLoading: isStatusLoading, isError: isStatusError } =
    useQuestionnaireStatusQuery()

  return (
    <ExpertReviewStatusContext.Provider
      value={{ statusData, isStatusLoading, isStatusError, entrySource }}
    >
      <ExpertReviewInitialStatusGate>
        <StrategyQuestionnaireProvider stageIdFromPathParam={stageIdFromPathParam}>
          <PreferencesProvider preferenceTypeIdFromPathParam={preferenceTypeIdFromPathParam}>
            {children}
          </PreferencesProvider>
        </StrategyQuestionnaireProvider>
      </ExpertReviewInitialStatusGate>
    </ExpertReviewStatusContext.Provider>
  )
}
